/**
 * When to remind a user that notifications are off — the decision, pure.
 *
 * The app cannot post an OS notification about this (no permission is the
 * whole situation), so the reminder is a small in-app banner. This module is
 * only the arithmetic of whether it should show; the component in
 * `components/notification-nudge.tsx` owns the pixels and the storage.
 *
 * Three gates, all required (owner spec, 2026-08-21):
 *
 *   1. Permission is not granted — the moment it is, there is nothing to say.
 *   2. At least `NOTIFICATION_NUDGE_MIN_INTERVAL_MS` since the last nudge, so
 *      the reminder stays an occasional tap on the shoulder rather than nag.
 *   3. NEW activity since the counters the last nudge saw — a reminder that
 *      "you are missing things" must only fire when there are, in fact,
 *      things. "New" means a counter went UP; going down is the user reading,
 *      and the floor-lowering below keeps a later, smaller wave of activity
 *      from hiding under an old high-water mark.
 *
 * No import from react-native, on purpose: the rules run as-is under node in
 * the test suite.
 */

/** Three hours. "Every 3 hours" is a floor, not a schedule: the nudge fires on
 *  the first new activity AFTER the interval, so its actual timing wanders
 *  with the activity itself. */
export const NOTIFICATION_NUDGE_MIN_INTERVAL_MS = 3 * 60 * 60 * 1000;

/** Device-scoped, deliberately not per-account: the permission it describes
 *  belongs to the device, and two accounts nagging on independent clocks
 *  would double the noise. */
export const NOTIFICATION_NUDGE_STORAGE_KEY = "opncue.notificationNudge";

/** The activity a nudge is allowed to point at, as client-visible counters. */
export type NudgeActivity = {
  unreadMessages: number;
  pendingCues: number;
  groupUnread: number;
};

export type NudgeMemory = {
  /** Epoch ms of the last shown nudge; 0 = never. */
  lastNudgeAt: number;
  /** The counters as of that nudge — the baseline "new" is measured against. */
  seen: NudgeActivity;
};

export const NO_ACTIVITY: NudgeActivity = {
  unreadMessages: 0,
  pendingCues: 0,
  groupUnread: 0,
};

const EMPTY_MEMORY: NudgeMemory = { lastNudgeAt: 0, seen: NO_ACTIVITY };

function countOf(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : 0;
}

/** Tolerant on purpose: corrupt or missing storage means "never nudged",
 *  which errs toward one extra reminder rather than a crash on launch. */
export function parseNudgeMemory(raw: string | null): NudgeMemory {
  if (!raw) return EMPTY_MEMORY;
  try {
    const parsed = JSON.parse(raw) as Partial<NudgeMemory>;
    return {
      lastNudgeAt: countOf(parsed.lastNudgeAt),
      seen: {
        unreadMessages: countOf(parsed.seen?.unreadMessages),
        pendingCues: countOf(parsed.seen?.pendingCues),
        groupUnread: countOf(parsed.seen?.groupUnread),
      },
    };
  } catch {
    return EMPTY_MEMORY;
  }
}

export function serializeNudgeMemory(memory: NudgeMemory): string {
  return JSON.stringify(memory);
}

/** Strictly greater on any counter. Equal or lower is not news. */
export function hasNewActivity(
  seen: NudgeActivity,
  current: NudgeActivity,
): boolean {
  return (
    current.unreadMessages > seen.unreadMessages ||
    current.pendingCues > seen.pendingCues ||
    current.groupUnread > seen.groupUnread
  );
}

/**
 * The baseline after the user has read things.
 *
 * Without this, a nudge at 5 unread followed by the user reading all 5 leaves
 * `seen.unreadMessages = 5` — and the next three messages (0 → 3) would never
 * count as new because 3 < 5. Per-counter minimum, so a drop in one counter
 * cannot mask a rise in another. `lastNudgeAt` is untouched: reading is not a
 * nudge.
 */
export function withLoweredFloor(
  memory: NudgeMemory,
  current: NudgeActivity,
): NudgeMemory {
  return {
    lastNudgeAt: memory.lastNudgeAt,
    seen: {
      unreadMessages: Math.min(memory.seen.unreadMessages, current.unreadMessages),
      pendingCues: Math.min(memory.seen.pendingCues, current.pendingCues),
      groupUnread: Math.min(memory.seen.groupUnread, current.groupUnread),
    },
  };
}

export function shouldShowNudge(input: {
  permissionGranted: boolean;
  now: number;
  memory: NudgeMemory;
  activity: NudgeActivity;
}): boolean {
  if (input.permissionGranted) return false;
  if (input.now - input.memory.lastNudgeAt < NOTIFICATION_NUDGE_MIN_INTERVAL_MS) {
    return false;
  }
  return hasNewActivity(input.memory.seen, input.activity);
}

/** The memory to persist the moment a nudge is put on screen. Stamped at SHOW
 *  time, not dismiss time, so a banner left ignored on screen does not earn a
 *  second one three hours later. */
export function shownMemory(now: number, activity: NudgeActivity): NudgeMemory {
  return { lastNudgeAt: now, seen: activity };
}
